import * as store from '../state/store';
import { onSamplerStatus } from '../audio/sampler';
import type { SamplerStatus } from '../audio/sampler';
import { setInputEnabled } from './input';
import { instrumentLabel, t } from '../i18n';

/**
 * Blocks play while a sampler instrument is still loading. On the synth engine
 * the keyboard is always live; on the sampler engine it stays disabled — dimmed,
 * with a spinner overlay — until the samples for the *current* instrument are
 * ready, so the first key-presses are never silently swallowed.
 */
export function installKeyboardGate(area: HTMLElement, overlay: HTMLElement, text: HTMLElement): void {
  let status: SamplerStatus = { kind: 'idle' };

  const update = (): void => {
    const samplerEngine = store.get('engine') === 'sampler';
    const readyForCurrent =
      status.kind === 'ready' && status.instrument === store.get('sampleInstrument');
    const playable = !samplerEngine || readyForCurrent;

    setInputEnabled(playable);
    area.classList.toggle('gated', !playable);
    overlay.hidden = playable;
    if (playable) return;

    if (status.kind === 'error') {
      overlay.classList.add('error');
      const msg = status.code === 'noSamples' ? t('status.errNoSamples') : t('status.errLoadFailed');
      text.textContent = t('overlay.error', { message: msg });
    } else if (status.kind === 'loading') {
      overlay.classList.remove('error');
      text.textContent = t('overlay.loading', {
        instrument: instrumentLabel(status.instrument),
        loaded: status.loaded,
        total: status.total,
      });
    } else {
      overlay.classList.remove('error');
      text.textContent = t('overlay.preparing');
    }
  };

  onSamplerStatus((st) => {
    status = st;
    update();
  });
  store.on(['engine', 'sampleInstrument', 'sampleDensity'], update);
  update();
}
